// src/ui/auth/OtpVerificationPage.jsx

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authService, { OtpRateLimitException } from '../../services/auth/authService';

export const OtpVerificationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const state = location.state || {};
  const phoneNumber = state.phoneNumber || '';
  const initialVerificationId = state.verificationId || '';
  const initialResendToken = state.resendToken || null;
  const isSignInFlow = state.isSignInFlow || false;

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [verificationId, setVerificationId] = useState(initialVerificationId);
  const [resendToken, setResendToken] = useState(initialResendToken);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [secondsRemaining, setSecondsRemaining] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [isShaking, setIsShaking] = useState(false);

  const inputRefs = useRef([]);
  const timerRef = useRef(null);

  // ── Timer ──────────────────────────────────────────────────────────────────

  const startResendTimer = useCallback(() => {
    setSecondsRemaining(60);
    setCanResend(false);

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // ── Focus first input on mount ────────────────────────────────────────────

  useEffect(() => {
    setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 300);
    startResendTimer();

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [startResendTimer]);

  // ── Handle OTP input change ──────────────────────────────────────────────

  const handleOtpChange = (index, value) => {
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-verify when all 6 digits are entered
    const fullOtp = newOtp.join('');
    if (fullOtp.length === 6) {
      verifyOtp(fullOtp);
    }
  };

  // ── Handle keydown for backspace ──────────────────────────────────────────

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // ── Handle paste ──────────────────────────────────────────────────────────

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otp];
    
    for (let i = 0; i < digits.length; i++) {
      newOtp[i] = digits[i];
    }
    
    setOtp(newOtp);
    
    // Focus the next empty input or the last one
    const nextIndex = Math.min(digits.length, 5);
    inputRefs.current[nextIndex]?.focus();
    
    if (digits.length === 6) {
      verifyOtp(digits);
    }
  };

  // ── Verify OTP ────────────────────────────────────────────────────────────

  const verifyOtp = async (code) => {
    if (code.length !== 6 || isLoading) return;

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const firebaseUser = await authService.verifyFirebaseOtp({
        verificationId: verificationId,
        smsCode: code,
      });

      // Check if user profile exists
      const profileExists = await authService.userProfileExists(firebaseUser.uid);

      if (profileExists) {
        navigate('/', { replace: true });
      } else {
        navigate('/add-profile', {
          state: {
            uid: firebaseUser.uid,
            phoneNumber: phoneNumber,
          },
          replace: true,
        });
      }
    } catch (error) {
      // Shake animation
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 400);

      let message = 'Verification failed. Please try again.';
      if (error.code === 'invalid-verification-code') {
        message = 'Invalid code. Please try again.';
      } else if (error.code === 'session-expired') {
        message = 'Code expired. Tap "Resend SMS" to get a new one.';
      } else if (error.code === 'invalid-verification-id') {
        message = 'Session invalid. Please go back and try again.';
      }

      setErrorMessage(message);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Resend code ───────────────────────────────────────────────────────────

  const handleResend = async () => {
    if (!canResend || isLoading) return;

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const otpData = await authService.resendFirebaseOtp(
        phoneNumber,
        resendToken
      );

      setVerificationId(otpData.verificationId);
      setResendToken(otpData.resendToken || null);
      setOtp(['', '', '', '', '', '']);
      startResendTimer();
      inputRefs.current[0]?.focus();
    } catch (error) {
      if (error instanceof OtpRateLimitException) {
        setErrorMessage(
          `Too many attempts. Please wait ${error.formattedRemaining} before requesting another code.`
        );
        setCanResend(false);
        
        // Re-arm button after cooldown
        setTimeout(() => {
          setCanResend(true);
        }, error.remaining);
        
        // Show countdown
        const waitSeconds = Math.floor(error.remaining / 1000);
        setSecondsRemaining(waitSeconds);
        
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        timerRef.current = setInterval(() => {
          setSecondsRemaining((prev) => {
            if (prev <= 1) {
              clearInterval(timerRef.current);
              setCanResend(true);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setErrorMessage('Failed to resend code. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Timer label ────────────────────────────────────────────────────────────

  const getTimerLabel = () => {
    if (secondsRemaining <= 0) return '';
    const m = Math.floor(secondsRemaining / 60);
    const s = secondsRemaining % 60;
    return m > 0 
      ? `${m}:${s.toString().padStart(2, '0')}`
      : `00:${s.toString().padStart(2, '0')}`;
  };

  // ── Render OTP boxes ──────────────────────────────────────────────────────

  const renderOtpBoxes = () => {
    return (
      <div className="flex justify-center gap-1.5">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            maxLength={1}
            value={digit}
            onChange={(e) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            className="w-12 h-14 text-center text-white text-xl font-medium bg-filatom-card rounded-xl outline-none focus:ring-2 focus:ring-filatom-primary/50 transition-all"
            style={{
              border: 'none',
            }}
            inputMode="numeric"
            autoComplete="one-time-code"
          />
        ))}
      </div>
    );
  };

  // ── Main render ────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-filatom-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md mx-auto">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="text-white p-2 -ml-2"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="mt-8 text-center">
          <h1 className="text-white text-xl font-semibold">Activate your account</h1>
          <p className="text-white/50 text-sm mt-2 leading-relaxed">
            We've sent a code by SMS to<br />
            <span className="text-white/70 font-medium">{phoneNumber}</span>
          </p>
        </div>

        {/* OTP Input */}
        <div
          className={`mt-8 p-4 bg-filatom-card rounded-xl transition-all ${
            isShaking ? 'animate-shake' : ''
          }`}
        >
          <p className="text-white/70 text-sm text-center">Enter 6-digit code</p>
          <div className="mt-4">
            {renderOtpBoxes()}
          </div>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
            <p className="text-red-400 text-sm text-center">{errorMessage}</p>
          </div>
        )}

        {/* Timer */}
        {getTimerLabel() && (
          <p className="text-white/70 text-center mt-4 text-sm">
            {getTimerLabel()}
          </p>
        )}

        {/* Actions */}
        <div className="mt-8 flex justify-around">
          <button
            onClick={handleResend}
            disabled={!canResend || isLoading}
            className={`text-sm font-medium transition-colors ${
              canResend && !isLoading
                ? 'text-filatom-primary hover:opacity-80'
                : 'text-white/40 cursor-not-allowed'
            }`}
          >
            Resend SMS
          </button>
          <button
            disabled
            className="text-white/40 text-sm cursor-not-allowed"
          >
            Activate via call
          </button>
        </div>

        {/* Loading overlay */}
        {isLoading && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-filatom-card p-6 rounded-xl flex flex-col items-center gap-4">
              <div className="w-10 h-10 border-3 border-filatom-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-white text-sm">Verifying...</p>
            </div>
          </div>
        )}
      </div>

      {/* Add shake animation styles */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); }
          20%, 40%, 60%, 80% { transform: translateX(10px); }
        }
        .animate-shake {
          animation: shake 0.4s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default OtpVerificationPage;